import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Globe, 
  CheckCircle, 
  AlertCircle,
  Lock,
  Unlock
} from 'lucide-react';
import LogoUpload from '../common/LogoUpload';
import LogoDisplay from '../common/LogoDisplay';
import SiteCountBadge from '../common/SiteCountBadge';
import PermissionButton from '../permissions/PermissionButton';
import { PERMISSIONS } from '@/types/permissions';

interface Tenant {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  is_blocked: boolean;
  blocked_at?: string;
  blocked_by?: number;
  blocked_by_name?: string;
  block_reason?: string;
  description?: string;
  address?: string;
  contact_email?: string;
  contact_phone?: string;
  logo?: string;
  created_at: string;
  sites_count?: number;
  users_count?: number;
}

interface TenantManagementProps {
  onTenantsUpdate: () => void;
}

const TenantManagement: React.FC<TenantManagementProps> = ({ onTenantsUpdate }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [viewingTenant, setViewingTenant] = useState<Tenant | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // États pour le blocage/déblocage
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [blockReason, setBlockReason] = useState('');

  // État pour le logo
  const [, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    is_active: true
  });

  useEffect(() => {
    console.log('🔍 TenantManagement monté, appel de fetchTenants');
    fetchTenants();
  }, []);

  // Fonction de gestion du logo
  const handleLogoChange = (file: File | null, logoUrl?: string) => {
    setLogoFile(file);
    setLogoPreview(logoUrl || null);
  };

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.local.ci/api/v1/sites/tenants/', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Réponse API tenants:', data);
        console.log('🔍 Type de data:', typeof data);
        console.log('🔍 Structure de data:', data);
        
        // Gestion robuste de la réponse
        let tenantsArray = [];
        if (Array.isArray(data)) {
          tenantsArray = data;
        } else if (data && Array.isArray(data.results)) {
          tenantsArray = data.results;
        } else if (data && Array.isArray(data.data)) {
          tenantsArray = data.data;
        } else {
          console.warn('⚠️ Format de réponse inattendu, utilisation de data tel quel');
          tenantsArray = data || [];
        }
        
        console.log('🔍 Tenants array final:', tenantsArray);
        setTenants(tenantsArray);
      } else {
        console.error('❌ Erreur API:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Détails erreur:', errorData);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de blocage
  const handleBlockTenant = async () => {
    if (!selectedTenant || !blockReason.trim()) return;
    
    try {
      const response = await fetch(`https://api.local.ci/api/v1/sites/tenants/${selectedTenant.id}/block/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: blockReason }),
      });
      
      if (response.ok) {
        setShowBlockModal(false);
        setSelectedTenant(null);
        setBlockReason('');
        onTenantsUpdate();
        fetchTenants();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.message || 'Impossible de bloquer le tenant'}`);
      }
    } catch (error) {
      console.error('Erreur lors du blocage du tenant:', error);
      alert('Erreur lors du blocage du tenant');
    }
  };

  // Fonction de déblocage
  const handleUnblockTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      const response = await fetch(`https://api.local.ci/api/v1/sites/tenants/${selectedTenant.id}/unblock/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setShowUnblockModal(false);
        setSelectedTenant(null);
        onTenantsUpdate();
        fetchTenants();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.message || 'Impossible de débloquer le tenant'}`);
      }
    } catch (error) {
      console.error('Erreur lors du déblocage du tenant:', error);
      alert('Erreur lors du déblocage du tenant');
    }
  };

  // Ouvrir modal de blocage
  const openBlockModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setBlockReason('');
    setShowBlockModal(true);
  };

  // Ouvrir modal de déblocage
  const openUnblockModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowUnblockModal(true);
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://api.local.ci/api/v1/sites/tenants/create/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setShowCreateForm(false);
        resetForm();
        onTenantsUpdate();
        fetchTenants();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.message || 'Impossible de créer le tenant'}`);
      }
    } catch (error) {
      console.error('Erreur lors de la création du tenant:', error);
      alert('Erreur lors de la création du tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenant) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`https://api.local.ci/api/v1/sites/tenants/${editingTenant.id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setEditingTenant(null);
        resetForm();
        onTenantsUpdate();
        fetchTenants();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.message || 'Impossible de modifier le tenant'}`);
      }
    } catch (error) {
      console.error('Erreur lors de la modification du tenant:', error);
      alert('Erreur lors de la modification du tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTenant = async (tenantId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce tenant ? Cette action est irréversible.')) return;
    
    try {
      const response = await fetch(`https://api.local.ci/api/v1/sites/tenants/${tenantId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      
      if (response.ok) {
        onTenantsUpdate();
        fetchTenants();
      } else {
        alert('Impossible de supprimer le tenant');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du tenant:', error);
      alert('Erreur lors de la suppression du tenant');
    }
  };

  const openEditForm = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      code: tenant.code,
      description: tenant.description || '',
      address: tenant.address || '',
      contact_email: tenant.contact_email || '',
      contact_phone: tenant.contact_phone || '',
      is_active: tenant.is_active
    });
    setLogoFile(null);
    setLogoPreview(tenant.logo || null);
  };

  const openCreateForm = () => {
    setShowCreateForm(true);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      address: '',
      contact_email: '',
      contact_phone: '',
      is_active: true
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const renderForm = (isEdit = false) => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            {isEdit ? 'Modifier le Tenant' : 'Créer un Nouveau Tenant'}
          </h3>
          
          <form onSubmit={isEdit ? handleUpdateTenant : handleCreateTenant} className="space-y-6">
            {/* Première ligne : Nom et Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du tenant *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="custom-form-field"
                  placeholder="Nom du tenant"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code du tenant *
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  className="custom-form-field"
                  placeholder="Code du tenant"
                />
              </div>
            </div>
            
            {/* Logo du tenant */}
            <div className="flex justify-center">
              <LogoUpload
                currentLogo={logoPreview}
                onLogoChange={handleLogoChange}
                size="md"
                className="max-w-xs"
              />
            </div>
            
            {/* Deuxième ligne : Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className="custom-textarea"
                placeholder="Description du tenant"
              />
            </div>
            
            {/* Troisième ligne : Adresse */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse complète
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                rows={3}
                className="custom-textarea"
                placeholder="Adresse complète"
              />
            </div>
            
            {/* Quatrième ligne : Contact téléphone et email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone de contact
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  className="custom-form-field"
                  placeholder="Téléphone de contact"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email de contact
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="custom-form-field"
                  placeholder="Email de contact"
                />
              </div>
            </div>
            
            {/* Cinquième ligne : Statut actif */}
            <div className="flex items-center p-4 bg-gray-50 rounded-lg border">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                style={{
                  height: '20px',
                  width: '20px',
                  color: '#ea580c',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              <label htmlFor="is_active" className="ml-3 text-sm font-medium text-gray-900">
                Tenant actif
              </label>
              <p className="ml-2 text-sm text-gray-500">
                (Un tenant inactif ne peut pas créer de nouveaux sites)
              </p>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex space-x-4 pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-md hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors"
                style={{
                  backgroundColor: isSubmitting ? '#9ca3af' : '#ea580c',
                  color: '#ffffff',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease-in-out',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Enregistrement...' : (isEdit ? 'Modifier le tenant' : 'Créer le tenant')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingTenant(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-400 font-medium transition-colors"
                style={{
                  backgroundColor: '#d1d5db',
                  color: '#374151',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Globe className="w-6 h-6 text-orange-500 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Gestion des Tenants</h2>
        </div>
        <PermissionButton
          permission={PERMISSIONS.TENANT_MANAGEMENT_CREATE}
          onClick={openCreateForm}
          className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center"
          disabledMessage="Permission insuffisante pour créer un tenant"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Tenant
        </PermissionButton>
      </div>

      {/* Liste des tenants */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.isArray(tenants) && tenants.length > 0 ? (
            tenants.map((tenant) => (
              <div key={tenant.id} className={`bg-white border rounded-lg p-6 hover:shadow-md transition-shadow ${
                tenant.is_blocked ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 text-gray-400 mr-2" />
                    <h3 className="font-medium text-gray-900">{tenant.name}</h3>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setViewingTenant(tenant)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Voir les détails"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEditForm(tenant)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {tenant.is_blocked ? (
                      <button
                        onClick={() => openUnblockModal(tenant)}
                        className="p-1 text-green-400 hover:text-green-600"
                        title="Débloquer"
                      >
                        <Unlock className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => openBlockModal(tenant)}
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Bloquer"
                      >
                        <Lock className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteTenant(tenant.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Logo du tenant */}
                <div className="flex justify-center mb-4">
                  <LogoDisplay
                    logoUrl={tenant.logo}
                    alt={`Logo de ${tenant.name}`}
                    size="md"
                    bordered
                  />
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium mr-2">Code:</span>
                    <span className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                      {tenant.code}
                    </span>
                  </div>
                  
                  {/* Nombre de sites associés */}
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium mr-2">Sites:</span>
                    <SiteCountBadge 
                      count={tenant.sites_count || 0} 
                      size="sm" 
                      variant="default"
                    />
                  </div>
                  
                  {tenant.description && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium mr-2">Description:</span>
                      <span className="text-gray-700">{tenant.description}</span>
                    </div>
                  )}
                  
                  {tenant.address && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Building2 className="w-4 h-4 mr-2" />
                      <span className="truncate">{tenant.address}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center text-sm">
                    <span className="font-medium mr-2">Statut:</span>
                    {tenant.is_blocked ? (
                      <span className="flex items-center text-red-600">
                        <Lock className="w-4 h-4 mr-1" />
                        Bloqué
                      </span>
                    ) : tenant.is_active ? (
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Actif
                      </span>
                    ) : (
                      <span className="flex items-center text-red-600">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Inactif
                      </span>
                    )}
                  </div>

                  {/* Informations de blocage */}
                  {tenant.is_blocked && (
                    <div className="bg-red-50 p-3 rounded-md border border-red-200">
                      <div className="text-sm text-red-800">
                        <div className="font-medium mb-1">Motif du blocage:</div>
                        <div className="text-red-700">{tenant.block_reason}</div>
                        {tenant.blocked_by_name && (
                          <div className="text-xs text-red-600 mt-1">
                            Bloqué par: {tenant.blocked_by_name}
                          </div>
                        )}
                        {tenant.blocked_at && (
                          <div className="text-xs text-red-600">
                            Le: {new Date(tenant.blocked_at).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {!Array.isArray(tenants) ? 'Erreur de chargement des tenants' : 'Aucun tenant créé'}
              </h3>
              <p className="text-gray-600 mb-4">
                {!Array.isArray(tenants) 
                  ? 'Impossible de charger la liste des tenants. Vérifiez la console pour plus de détails.'
                  : 'Commencez par créer votre premier tenant pour organiser vos sites.'
                }
              </p>
              {Array.isArray(tenants) && (
                <PermissionButton
                  permission={PERMISSIONS.TENANT_MANAGEMENT_CREATE}
                  onClick={openCreateForm}
                  className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
                  disabledMessage="Permission insuffisante pour créer un tenant"
                >
                  Créer le premier tenant
                </PermissionButton>
              )}
            </div>
          )}
        </div>
      )}

      {Array.isArray(tenants) && tenants.length === 0 && !loading && (
        <div className="text-center py-12">
          <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun tenant créé</h3>
          <p className="text-gray-600 mb-4">
            Commencez par créer votre premier tenant pour organiser vos sites.
          </p>
          <PermissionButton
            permission={PERMISSIONS.TENANT_MANAGEMENT_CREATE}
            onClick={openCreateForm}
            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
            disabledMessage="Permission insuffisante pour créer un tenant"
          >
            Créer le premier tenant
          </PermissionButton>
        </div>
      )}

      {/* Modals */}
      {showCreateForm && renderForm(false)}
      {editingTenant && renderForm(true)}
      
      {/* Modal de visualisation */}
      {viewingTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Détails du Tenant</h3>
              
              {/* Logo du tenant */}
              <div className="flex justify-center mb-4">
                <LogoDisplay
                  logoUrl={viewingTenant.logo}
                  alt={`Logo de ${viewingTenant.name}`}
                  size="lg"
                  bordered
                />
              </div>
              
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700">Nom:</span>
                  <p className="text-gray-900">{viewingTenant.name}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Code:</span>
                  <p className="text-gray-900 font-mono">{viewingTenant.code}</p>
                </div>
                
                {/* Nombre de sites associés */}
                <div>
                  <span className="font-medium text-gray-700">Sites associés:</span>
                  <p className="text-gray-900">
                    <SiteCountBadge 
                      count={viewingTenant.sites_count || 0} 
                      size="md" 
                      variant="default"
                    />
                  </p>
                </div>
                
                {viewingTenant.description && (
                  <div>
                    <span className="font-medium text-gray-700">Description:</span>
                    <p className="text-gray-900">{viewingTenant.description}</p>
                  </div>
                )}
                {viewingTenant.address && (
                  <div>
                    <span className="font-medium text-gray-700">Adresse:</span>
                    <p className="text-gray-900">{viewingTenant.address}</p>
                  </div>
                )}
                {viewingTenant.contact_email && (
                  <div>
                    <span className="font-medium text-gray-700">Email:</span>
                    <p className="text-gray-900">{viewingTenant.contact_email}</p>
                  </div>
                )}
                {viewingTenant.contact_phone && (
                  <div>
                    <span className="font-medium text-gray-700">Téléphone:</span>
                    <p className="text-gray-900">{viewingTenant.contact_phone}</p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Statut:</span>
                  <p className={`${viewingTenant.is_blocked ? 'text-red-600' : viewingTenant.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {viewingTenant.is_blocked ? 'Bloqué' : viewingTenant.is_active ? 'Actif' : 'Inactif'}
                  </p>
                </div>
                {viewingTenant.is_blocked && viewingTenant.block_reason && (
                  <div>
                    <span className="font-medium text-gray-700">Motif du blocage:</span>
                    <p className="text-red-700">{viewingTenant.block_reason}</p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Créé le:</span>
                  <p className="text-gray-900">{new Date(viewingTenant.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setViewingTenant(null)}
                  className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de blocage */}
      {showBlockModal && selectedTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-red-900 mb-4">Bloquer le Tenant</h3>
              <p className="text-sm text-gray-600 mb-4">
                Êtes-vous sûr de vouloir bloquer le tenant <strong>{selectedTenant.name}</strong> ?
              </p>
              
              {/* Avertissement si le tenant a des sites */}
              {selectedTenant.sites_count && selectedTenant.sites_count > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                  <div className="flex items-center text-sm text-yellow-800">
                    <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    <div>
                      <div className="font-medium mb-1">⚠️ Attention !</div>
                      <div>Ce tenant a <strong>{selectedTenant.sites_count} site{selectedTenant.sites_count !== 1 ? 's' : ''}</strong> associé{selectedTenant.sites_count !== 1 ? 's' : ''}.</div>
                      <div className="text-xs mt-1">Le blocage du tenant affectera également l'accès à ces sites.</div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif du blocage *
                </label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
                  placeholder="Expliquez la raison du blocage..."
                  required
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleBlockTenant}
                  disabled={!blockReason.trim()}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Bloquer
                </button>
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setSelectedTenant(null);
                    setBlockReason('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de déblocage */}
      {showUnblockModal && selectedTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-green-900 mb-4">Débloquer le Tenant</h3>
              <p className="text-sm text-gray-600 mb-4">
                Êtes-vous sûr de vouloir débloquer le tenant <strong>{selectedTenant.name}</strong> ?
              </p>
              
              {/* Information sur les sites associés */}
              {selectedTenant.sites_count && selectedTenant.sites_count > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <div className="flex items-center text-sm text-blue-800">
                    <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                    <div>
                      <div className="font-medium mb-1">ℹ️ Sites associés</div>
                      <div>Ce tenant a <strong>{selectedTenant.sites_count} site{selectedTenant.sites_count !== 1 ? 's' : ''}</strong> associé{selectedTenant.sites_count !== 1 ? 's' : ''}.</div>
                      <div className="text-xs mt-1">Le déblocage restaurera l'accès à tous ces sites.</div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-green-50 p-3 rounded-md border border-green-200 mb-4">
                <div className="text-sm text-green-800">
                  <div className="font-medium mb-1">Motif actuel du blocage:</div>
                  <div className="text-green-700">{selectedTenant.block_reason}</div>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleUnblockTenant}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Débloquer
                </button>
                <button
                  onClick={() => {
                    setShowUnblockModal(false);
                    setSelectedTenant(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
